import { JobCompletionChecklist } from "./JobCompletionChecklist";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AssignmentCard } from "@/components/dispatch/AssignmentCard";
import { MaterialCalculator } from "@/components/production/MaterialCalculator";
import { JobInvoices } from "@/components/invoices/JobInvoices";
import { AlertCircle, CheckCircle2, Users, Package, ClipboardCheck, Calendar, Plus } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

interface ProductionTabProps {
  jobId: string;
  orgId: string;
  jobStatus: string;
  onChecklistComplete?: () => void;
}

export const ProductionTab = ({ jobId, orgId, jobStatus, onChecklistComplete }: ProductionTabProps) => {
  const navigate = useNavigate();
  const [crewAssignments, setCrewAssignments] = useState<any[]>([]);
  const [materialOrders, setMaterialOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalSquareFeet, setTotalSquareFeet] = useState<number>(0);

  const isInProduction = jobStatus === "Production";
  const isQualityCheck = jobStatus === "Quality Check";
  const isPastProduction = ["Invoiced", "Paid", "Warranty Active"].includes(jobStatus);
  const isChecklistActive = isQualityCheck || isPastProduction;

  useEffect(() => {
    loadProductionData();
  }, [jobId, orgId]);

  const loadProductionData = async () => {
    setLoading(true);
    try {
      // Fetch crew assignments
      const { data: assignments, error: assignmentsError } = await supabase
        .from('crew_assignments')
        .select(`
          *,
          crew:crews(id, name),
          job:jobs(property_address)
        `)
        .eq('job_id', jobId)
        .eq('org_id', orgId)
        .order('scheduled_date', { ascending: false });

      if (assignmentsError) throw assignmentsError;
      setCrewAssignments(assignments || []);

      // Fetch material orders
      const { data: orders, error: ordersError } = await supabase
        .from('material_orders')
        .select('*')
        .eq('job_id', jobId)
        .eq('org_id', orgId)
        .order('created_at', { ascending: false });

      if (ordersError) throw ordersError;
      setMaterialOrders(orders || []);

      // Fetch total square feet from measurements
      const { data: measurements, error: measurementsError } = await supabase
        .from('measurements_roof')
        .select('total_area')
        .eq('job_id', jobId)
        .eq('org_id', orgId)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (!measurementsError && measurements) {
        setTotalSquareFeet(measurements.total_area || 0);
      }
    } catch (error: any) {
      console.error('Error loading production data:', error);
      toast.error('Failed to load production data');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignmentStatusUpdate = async (assignmentId: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from('crew_assignments')
        .update({ status: newStatus })
        .eq('id', assignmentId);

      if (error) throw error;
      toast.success('Assignment status updated');
      loadProductionData();
    } catch (error: any) {
      console.error('Error updating assignment:', error);
      toast.error('Failed to update assignment');
    }
  };

  const handleMaterialOrderCreated = () => {
    loadProductionData();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'bg-muted';
      case 'ordered': return 'bg-blue-500/10 text-blue-700 dark:text-blue-400';
      case 'delivered': return 'bg-green-500/10 text-green-700 dark:text-green-400';
      default: return 'bg-muted';
    }
  };

  const getProgressSteps = () => {
    const steps = [
      { label: 'Not Started', active: !['Production', 'Quality Check', 'Invoiced', 'Paid', 'Warranty Active'].includes(jobStatus) },
      { label: 'Production', active: isInProduction },
      { label: 'Quality Check', active: isQualityCheck },
      { label: 'Complete', active: isPastProduction },
    ];
    return steps;
  };

  return (
    <div className="space-y-6 p-6">
      {/* Production Progress Tracker */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Production Progress
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between gap-2">
            {getProgressSteps().map((step, index) => (
              <div key={index} className="flex-1 flex items-center">
                <div className={`flex-1 text-center py-2 px-4 rounded-lg transition-colors ${
                  step.active ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                }`}>
                  <div className="text-sm font-medium">{step.label}</div>
                </div>
                {index < getProgressSteps().length - 1 && (
                  <div className="w-8 h-0.5 bg-border mx-1" />
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Status Banner */}
      {isInProduction && (
        <Card className="border-blue-200 bg-blue-50 dark:bg-blue-950/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-blue-700 dark:text-blue-400">
              <AlertCircle className="w-5 h-5" />
              Job In Production
            </CardTitle>
            <CardDescription>
              This job is currently being worked on. Assign crews and track material orders below.
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      {isQualityCheck && (
        <Card className="border-yellow-200 bg-yellow-50 dark:bg-yellow-950/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-yellow-700 dark:text-yellow-400">
              <AlertCircle className="w-5 h-5" />
              Quality Check Required
            </CardTitle>
            <CardDescription>
              Complete the checklist below to verify all work is finished before invoicing.
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      {isPastProduction && (
        <Card className="border-green-200 bg-green-50 dark:bg-green-950/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-700 dark:text-green-400">
              <CheckCircle2 className="w-5 h-5" />
              Production Complete
            </CardTitle>
            <CardDescription>
              This job has completed production and quality checks.
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      {/* Crew Assignments Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Crew Assignments
              </CardTitle>
              <CardDescription>Scheduled crews and production timeline</CardDescription>
            </div>
            <Button size="sm" onClick={() => navigate(`/dispatch?jobId=${jobId}`)}>
              <Plus className="w-4 h-4 mr-2" />
              Assign Crew
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading assignments...</div>
          ) : crewAssignments.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No crews assigned yet. Click "Assign Crew" to schedule production.
            </div>
          ) : (
            <div className="space-y-3">
              {crewAssignments.map((assignment) => (
                <AssignmentCard
                  key={assignment.id}
                  assignment={assignment}
                  crewColor="border-l-primary"
                  onStatusUpdate={handleAssignmentStatusUpdate}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Material Orders Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="w-5 h-5" />
            Materials & Orders
          </CardTitle>
          <CardDescription>Calculate and order materials for this job</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Material Calculator */}
          <MaterialCalculator
            jobId={jobId}
            orgId={orgId}
            totalSquareFeet={totalSquareFeet}
            onOrderCreated={handleMaterialOrderCreated}
          />

          {/* Existing Material Orders */}
          {materialOrders.length > 0 && (
            <div className="space-y-3">
              <h4 className="font-semibold text-sm">Existing Orders</h4>
              {materialOrders.map((order) => (
                <Card key={order.id} className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-semibold">{order.supplier}</span>
                        <Badge className={getStatusColor(order.status)}>
                          {order.status}
                        </Badge>
                        {order.ai_suggested && (
                          <Badge variant="outline" className="text-xs">AI Suggested</Badge>
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        <div>Total: ${order.total_cost?.toFixed(2)}</div>
                        <div className="text-xs mt-1">
                          {order.items && typeof order.items === 'object' && 
                            `${Object.keys(order.items).length} items`
                          }
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* QA Completion Checklist - Always Visible */}
      <Card className={!isChecklistActive ? 'opacity-60' : ''}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5" />
            QA Completion Checklist
          </CardTitle>
          <CardDescription>
            {isChecklistActive 
              ? 'Complete all items before moving to invoice' 
              : 'This checklist will become active when job moves to Quality Check'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isChecklistActive ? (
            <JobCompletionChecklist
              jobId={jobId}
              orgId={orgId}
              onComplete={onChecklistComplete}
            />
          ) : (
            <div className="space-y-3 pointer-events-none">
              <div className="flex items-center gap-2 text-sm">
                <div className="w-4 h-4 border-2 border-muted rounded" />
                <span className="text-muted-foreground">Final photos uploaded</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <div className="w-4 h-4 border-2 border-muted rounded" />
                <span className="text-muted-foreground">Site cleanup complete</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <div className="w-4 h-4 border-2 border-muted rounded" />
                <span className="text-muted-foreground">Customer walkthrough done</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <div className="w-4 h-4 border-2 border-muted rounded" />
                <span className="text-muted-foreground">Punch list items addressed</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Invoices Section - Show after Quality Check */}
      {(isQualityCheck || isPastProduction) && (
        <Card className="p-6">
          <JobInvoices 
            jobId={jobId} 
            orgId={orgId}
            customerEmail={undefined}
          />
        </Card>
      )}
    </div>
  );
};
